import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  TextInput,
  Modal,
  ActivityIndicator,
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { logActivity } from "@/lib/activity-log";
import { createScoreNotifications } from "@/modules/notifications/utils/createScoreNotifications";

/**
 * Team with score info
 */
interface TeamWithScore {
  id: string;
  name: string;
  color: string | null;
  avatar_url: string | null;
  total_score: number;
  member_count: number;
}

/**
 * User with score info
 */
interface UserWithScore {
  id: string;
  full_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
  total_points: number;
  team_id: string | null;
  team_name: string | null;
  team_color: string | null;
}

type TabType = "teams" | "individuals";
type AwardType = "add" | "subtract";

/**
 * Punten Toekennen Screen - Award points to teams or individuals
 */
export default function PuntenToekennenScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend, profile, roles } = useAuth();

  // Data state
  const [teams, setTeams] = useState<TeamWithScore[]>([]);
  const [users, setUsers] = useState<UserWithScore[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("teams");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamWithScore | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithScore | null>(null);
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [awardType, setAwardType] = useState<AwardType>("add");
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Fetch teams and users
   */
  const fetchData = useCallback(async () => {
    if (!activeWeekend?.id) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch teams
      const { data: teamsData, error: teamsError } = await supabase
        .from("teams")
        .select(`
          id,
          name,
          color,
          avatar_url,
          total_score
        `)
        .eq("weekend_id", activeWeekend.id)
        .order("total_score", { ascending: false });

      if (teamsError) throw teamsError;

      // Fetch team member counts
      const teamIds = teamsData?.map((t) => t.id) || [];
      let memberCounts: Record<string, number> = {};

      if (teamIds.length > 0) {
        const { data: members } = await supabase
          .from("team_members")
          .select("team_id")
          .in("team_id", teamIds);

        (members || []).forEach((m) => {
          memberCounts[m.team_id] = (memberCounts[m.team_id] || 0) + 1;
        });
      }

      setTeams(
        (teamsData || []).map((t) => ({
          ...t,
          total_score: t.total_score || 0,
          member_count: memberCounts[t.id] || 0,
        }))
      );

      // Fetch users with their team info and individual scores
      const { data: usersData, error: usersError } = await supabase
        .from("profiles")
        .select(`
          id,
          full_name,
          nickname,
          avatar_url
        `)
        .order("full_name");

      if (usersError) throw usersError;

      // Fetch team memberships
      const { data: teamMemberships } = await supabase
        .from("team_members")
        .select(`
          user_id,
          teams:team_id (
            id,
            name,
            color
          )
        `)
        .in("team_id", teamIds);

      // Map user to team
      const userTeamMap: Record<string, { team_id: string; team_name: string; team_color: string | null }> = {};
      (teamMemberships || []).forEach((tm: any) => {
        if (tm.teams) {
          userTeamMap[tm.user_id] = {
            team_id: tm.teams.id,
            team_name: tm.teams.name,
            team_color: tm.teams.color,
          };
        }
      });

      // Fetch individual scores from score_history
      const { data: individualScores } = await supabase
        .from("score_history")
        .select("awarded_to, points")
        .not("awarded_to", "is", null);

      const userScoreMap: Record<string, number> = {};
      (individualScores || []).forEach((s) => {
        if (s.awarded_to) {
          userScoreMap[s.awarded_to] = (userScoreMap[s.awarded_to] || 0) + s.points;
        }
      });

      setUsers(
        (usersData || []).map((u) => ({
          ...u,
          total_points: userScoreMap[u.id] || 0,
          team_id: userTeamMap[u.id]?.team_id || null,
          team_name: userTeamMap[u.id]?.team_name || null,
          team_color: userTeamMap[u.id]?.team_color || null,
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      Alert.alert("Fout", "Kon gegevens niet laden");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeWeekend?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  /**
   * Open award modal for team
   */
  const handleTeamPress = (team: TeamWithScore) => {
    setSelectedTeam(team);
    setSelectedUser(null);
    setPoints("");
    setReason("");
    setAwardType("add");
    setShowModal(true);
  };

  /**
   * Open award modal for user
   */
  const handleUserPress = (user: UserWithScore) => {
    setSelectedUser(user);
    setSelectedTeam(null);
    setPoints("");
    setReason("");
    setAwardType("add");
    setShowModal(true);
  };

  /**
   * Award points
   */
  const handleAwardPoints = async () => {
    const pointsNum = parseInt(points, 10);
    if (isNaN(pointsNum) || pointsNum <= 0) {
      Alert.alert("Fout", "Voer een geldig aantal punten in (groter dan 0)");
      return;
    }

    if (!reason.trim()) {
      Alert.alert("Fout", "Voer een reden in voor de punten");
      return;
    }

    if (!selectedTeam && !selectedUser) {
      Alert.alert("Fout", "Selecteer eerst een team of gebruiker");
      return;
    }

    setIsSaving(true);

    try {
      const actualPoints = awardType === "add" ? pointsNum : -pointsNum;

      if (selectedTeam) {
        // Award points to team
        const currentScore = selectedTeam.total_score;
        const newScore = currentScore + actualPoints;

        // Insert score history
        const { error: historyError } = await supabase
          .from("score_history")
          .insert({
            team_id: selectedTeam.id,
            points: actualPoints,
            previous_total: currentScore,
            new_total: newScore,
            reason: reason.trim(),
            game_type: "manual",
            awarded_by: profile?.id,
          });

        if (historyError) throw historyError;

        // Update team total score
        const { error: updateError } = await supabase
          .from("teams")
          .update({ total_score: newScore })
          .eq("id", selectedTeam.id);

        if (updateError) throw updateError;

        // Log activity
        if (profile?.id) {
          await logActivity({
            user_id: profile.id,
            action_type: awardType === "add" ? "score.award" : "score.deduct",
            action_category: "score",
            target_type: "team",
            target_id: selectedTeam.id,
            target_name: selectedTeam.name,
            metadata: {
              points: actualPoints,
              reason: reason.trim(),
              previous_total: currentScore,
              new_total: newScore,
            },
            weekend_id: activeWeekend?.id,
          });

          // Create in-app notifications for team members
          await createScoreNotifications({
            teamId: selectedTeam.id,
            teamName: selectedTeam.name,
            points: actualPoints,
            reason: reason.trim(),
            awardedBy: profile.id,
            weekendId: activeWeekend?.id,
          });
        }

        Alert.alert(
          "Succes",
          `${awardType === "add" ? "+" : ""}${actualPoints} punten ${awardType === "add" ? "toegekend aan" : "afgetrokken van"} ${selectedTeam.name}`,
          [{ text: "OK" }]
        );
      } else if (selectedUser) {
        // Award points to individual user
        // Need to find user's team for score_history
        const teamId = selectedUser.team_id;

        if (!teamId) {
          Alert.alert("Fout", "Deze gebruiker zit niet in een team. Individuele punten kunnen alleen worden toegekend aan teamleden.");
          setIsSaving(false);
          return;
        }

        // Get current team score
        const { data: teamData } = await supabase
          .from("teams")
          .select("total_score")
          .eq("id", teamId)
          .single();

        const currentTeamScore = teamData?.total_score || 0;
        const newTeamScore = currentTeamScore + actualPoints;

        // Insert score history with awarded_to
        const { error: historyError } = await supabase
          .from("score_history")
          .insert({
            team_id: teamId,
            points: actualPoints,
            previous_total: currentTeamScore,
            new_total: newTeamScore,
            reason: reason.trim(),
            game_type: "manual",
            awarded_by: profile?.id,
            awarded_to: selectedUser.id,
          });

        if (historyError) throw historyError;

        // Update team total score
        const { error: updateError } = await supabase
          .from("teams")
          .update({ total_score: newTeamScore })
          .eq("id", teamId);

        if (updateError) throw updateError;

        // Log activity
        if (profile?.id) {
          await logActivity({
            user_id: profile.id,
            action_type: awardType === "add" ? "score.award" : "score.deduct",
            action_category: "score",
            target_type: "user",
            target_id: selectedUser.id,
            target_name: selectedUser.full_name || selectedUser.nickname || "Onbekend",
            metadata: {
              points: actualPoints,
              reason: reason.trim(),
              team_id: teamId,
              team_name: selectedUser.team_name,
            },
            weekend_id: activeWeekend?.id,
          });

          // Create in-app notifications for the user and teammates
          await createScoreNotifications({
            teamId: teamId,
            teamName: selectedUser.team_name || "Team",
            points: actualPoints,
            reason: reason.trim(),
            awardedBy: profile.id,
            awardedToUserId: selectedUser.id,
            awardedToUserName: selectedUser.full_name || selectedUser.nickname || "Gebruiker",
            weekendId: activeWeekend?.id,
          });
        }

        Alert.alert(
          "Succes",
          `${awardType === "add" ? "+" : ""}${actualPoints} punten ${awardType === "add" ? "toegekend aan" : "afgetrokken van"} ${selectedUser.full_name || selectedUser.nickname}`,
          [{ text: "OK" }]
        );
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error("Error awarding points:", error);
      Alert.alert("Fout", "Kon punten niet toekennen");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Get initials from name
   */
  const getInitials = (name: string | null): string => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (!activeWeekend) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="calendar-outline" size={64} color={colors.textTertiary} />
        <Text className="text-lg font-medium mt-4 text-center" style={{ color: colors.text }}>
          Geen actief weekend
        </Text>
        <Text className="mt-2 text-center" style={{ color: colors.textSecondary }}>
          Er moet eerst een weekend actief zijn om punten toe te kennen.
        </Text>
      </SafeAreaView>
    );
  }

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-4" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      {/* Tab Switcher */}
      <View className="flex-row px-4 py-3">
        <TouchableOpacity
          className="flex-1 py-3 rounded-xl mr-2 items-center"
          style={{
            backgroundColor: activeTab === "teams" ? "#F97316" : colors.surfaceSecondary,
          }}
          activeOpacity={0.7}
          onPress={() => setActiveTab("teams")}
          accessibilityRole="tab"
          accessibilityState={{ selected: activeTab === "teams" }}
        >
          <Text
            className="font-semibold"
            style={{
              color: activeTab === "teams" ? "#FFFFFF" : colors.textSecondary,
            }}
          >
            Teams ({teams.length})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          className="flex-1 py-3 rounded-xl ml-2 items-center"
          style={{
            backgroundColor: activeTab === "individuals" ? "#F97316" : colors.surfaceSecondary,
          }}
          activeOpacity={0.7}
          onPress={() => setActiveTab("individuals")}
          accessibilityRole="tab"
          accessibilityState={{ selected: activeTab === "individuals" }}
        >
          <Text
            className="font-semibold"
            style={{
              color: activeTab === "individuals" ? "#FFFFFF" : colors.textSecondary,
            }}
          >
            Individuen ({users.length})
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        className="flex-1 px-4"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Info Banner */}
        <View
          className="rounded-xl p-4 mb-4 flex-row items-start"
          style={{ backgroundColor: colors.primaryLight }}
        >
          <Ionicons name="information-circle" size={20} color={colors.primary} />
          <Text className="flex-1 ml-2 text-sm" style={{ color: colors.textSecondary }}>
            Tik op een {activeTab === "teams" ? "team" : "persoon"} om punten toe te kennen of af te trekken.
            Voeg altijd een reden toe zodat iedereen weet waarom punten zijn toegekend.
          </Text>
        </View>

        {/* Teams Tab */}
        {activeTab === "teams" && (
          <View className="pb-8">
            {teams.length === 0 ? (
              <View className="py-12 items-center">
                <Ionicons name="people-outline" size={48} color={colors.border} />
                <Text className="mt-4 text-center" style={{ color: colors.textSecondary }}>
                  Nog geen teams aangemaakt
                </Text>
              </View>
            ) : (
              teams.map((team) => {
                const teamColor = team.color || colors.primary;
                return (
                  <TouchableOpacity
                    key={team.id}
                    onPress={() => handleTeamPress(team)}
                    className="rounded-xl p-4 mb-3 flex-row items-center active:opacity-70"
                    style={{
                      backgroundColor: colors.surface,
                      borderWidth: 1,
                      borderColor: colors.border,
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`${team.name}, ${team.total_score} punten`}
                  >
                    {/* Team Avatar */}
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center mr-3 overflow-hidden"
                      style={{ backgroundColor: `${teamColor}20` }}
                    >
                      {team.avatar_url ? (
                        team.avatar_url.startsWith("http") ? (
                          <Image
                            source={{ uri: team.avatar_url }}
                            className="w-full h-full"
                            resizeMode="cover"
                          />
                        ) : (
                          <Text className="text-2xl">{team.avatar_url}</Text>
                        )
                      ) : (
                        <Text className="font-bold text-lg" style={{ color: teamColor }}>
                          {team.name.charAt(0).toUpperCase()}
                        </Text>
                      )}
                    </View>

                    {/* Team Info */}
                    <View className="flex-1">
                      <Text className="font-semibold text-base" style={{ color: colors.text }}>
                        {team.name}
                      </Text>
                      <Text className="text-sm" style={{ color: colors.textSecondary }}>
                        {team.member_count} {team.member_count === 1 ? "lid" : "leden"}
                      </Text>
                    </View>

                    {/* Score */}
                    <View className="items-end mr-2">
                      <Text className="font-bold text-xl" style={{ color: "#F97316" }}>
                        {team.total_score.toLocaleString()}
                      </Text>
                      <Text className="text-xs" style={{ color: colors.textTertiary }}>
                        punten
                      </Text>
                    </View>

                    <Ionicons name="add-circle" size={28} color="#F97316" />
                  </TouchableOpacity>
                );
              })
            )}
          </View>
        )}

        {/* Individuals Tab */}
        {activeTab === "individuals" && (
          <View className="pb-8">
            {users.length === 0 ? (
              <View className="py-12 items-center">
                <Ionicons name="person-outline" size={48} color={colors.border} />
                <Text className="mt-4 text-center" style={{ color: colors.textSecondary }}>
                  Geen gebruikers gevonden
                </Text>
              </View>
            ) : (
              users.map((user) => (
                <TouchableOpacity
                  key={user.id}
                  onPress={() => handleUserPress(user)}
                  className="rounded-xl p-4 mb-3 flex-row items-center active:opacity-70"
                  style={{
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: user.team_id ? colors.border : `${colors.error}40`,
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`${user.full_name || user.nickname}, ${user.total_points} punten`}
                >
                  {/* Avatar */}
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center mr-3 overflow-hidden"
                    style={{ backgroundColor: colors.primaryLight }}
                  >
                    {user.avatar_url ? (
                      <Image
                        source={{ uri: user.avatar_url }}
                        className="w-full h-full"
                        resizeMode="cover"
                      />
                    ) : (
                      <Text className="font-bold" style={{ color: colors.primary }}>
                        {getInitials(user.nickname || user.full_name)}
                      </Text>
                    )}
                  </View>

                  {/* User Info */}
                  <View className="flex-1">
                    <Text className="font-semibold" style={{ color: colors.text }}>
                      {user.nickname || user.full_name || "Onbekend"}
                    </Text>
                    {user.team_name ? (
                      <View className="flex-row items-center mt-0.5">
                        <View
                          className="w-2 h-2 rounded-full mr-1.5"
                          style={{ backgroundColor: user.team_color || colors.primary }}
                        />
                        <Text className="text-sm" style={{ color: colors.textSecondary }}>
                          {user.team_name}
                        </Text>
                      </View>
                    ) : (
                      <Text className="text-sm" style={{ color: colors.error }}>
                        Geen team
                      </Text>
                    )}
                  </View>

                  {/* Score */}
                  <View className="items-end mr-2">
                    <Text className="font-bold text-lg" style={{ color: "#F97316" }}>
                      {user.total_points.toLocaleString()}
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textTertiary }}>
                      punten
                    </Text>
                  </View>

                  <Ionicons
                    name="add-circle"
                    size={24}
                    color={user.team_id ? "#F97316" : colors.textTertiary}
                  />
                </TouchableOpacity>
              ))
            )}
          </View>
        )}
      </ScrollView>

      {/* Award Points Modal */}
      <Modal
        visible={showModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowModal(false)}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          className="flex-1"
          style={{ backgroundColor: colors.background }}
        >
          {/* Modal Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: colors.border }}
          >
            <TouchableOpacity onPress={() => setShowModal(false)}>
              <Text style={{ color: colors.textSecondary }}>Annuleren</Text>
            </TouchableOpacity>
            <Text className="font-semibold text-lg" style={{ color: colors.text }}>
              Punten Toekennen
            </Text>
            <TouchableOpacity onPress={handleAwardPoints} disabled={isSaving}>
              {isSaving ? (
                <ActivityIndicator size="small" color="#F97316" />
              ) : (
                <Text className="font-semibold" style={{ color: "#F97316" }}>
                  Opslaan
                </Text>
              )}
            </TouchableOpacity>
          </View>

          <ScrollView className="flex-1 px-4" showsVerticalScrollIndicator={false}>
            {/* Selected Target */}
            <View className="mt-6">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                {selectedTeam ? "Team" : "Persoon"}
              </Text>
              <View
                className="rounded-xl p-4 flex-row items-center"
                style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
              >
                {selectedTeam ? (
                  <>
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center mr-3 overflow-hidden"
                      style={{ backgroundColor: `${selectedTeam.color || colors.primary}20` }}
                    >
                      {selectedTeam.avatar_url ? (
                        selectedTeam.avatar_url.startsWith("http") ? (
                          <Image
                            source={{ uri: selectedTeam.avatar_url }}
                            className="w-full h-full"
                            resizeMode="cover"
                          />
                        ) : (
                          <Text className="text-2xl">{selectedTeam.avatar_url}</Text>
                        )
                      ) : (
                        <Text
                          className="font-bold text-lg"
                          style={{ color: selectedTeam.color || colors.primary }}
                        >
                          {selectedTeam.name.charAt(0).toUpperCase()}
                        </Text>
                      )}
                    </View>
                    <View className="flex-1">
                      <Text className="font-semibold text-base" style={{ color: colors.text }}>
                        {selectedTeam.name}
                      </Text>
                      <Text className="text-sm" style={{ color: colors.textSecondary }}>
                        Huidige score: {selectedTeam.total_score} punten
                      </Text>
                    </View>
                  </>
                ) : selectedUser ? (
                  <>
                    <View
                      className="w-12 h-12 rounded-full items-center justify-center mr-3 overflow-hidden"
                      style={{ backgroundColor: colors.primaryLight }}
                    >
                      {selectedUser.avatar_url ? (
                        <Image
                          source={{ uri: selectedUser.avatar_url }}
                          className="w-full h-full"
                          resizeMode="cover"
                        />
                      ) : (
                        <Text className="font-bold" style={{ color: colors.primary }}>
                          {getInitials(selectedUser.nickname || selectedUser.full_name)}
                        </Text>
                      )}
                    </View>
                    <View className="flex-1">
                      <Text className="font-semibold text-base" style={{ color: colors.text }}>
                        {selectedUser.nickname || selectedUser.full_name || "Onbekend"}
                      </Text>
                      <Text className="text-sm" style={{ color: colors.textSecondary }}>
                        {selectedUser.team_name || "Geen team"} • {selectedUser.total_points} punten
                      </Text>
                    </View>
                  </>
                ) : null}
              </View>
            </View>

            {/* Award Type */}
            <View className="mt-6">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Type
              </Text>
              <View className="flex-row">
                <TouchableOpacity
                  className="flex-1 py-3 rounded-xl mr-2 items-center flex-row justify-center"
                  style={{
                    backgroundColor: awardType === "add" ? "#10B981" : colors.surfaceSecondary,
                  }}
                  onPress={() => setAwardType("add")}
                >
                  <Ionicons
                    name="add-circle"
                    size={20}
                    color={awardType === "add" ? "#FFFFFF" : colors.textSecondary}
                  />
                  <Text
                    className="font-semibold ml-2"
                    style={{ color: awardType === "add" ? "#FFFFFF" : colors.textSecondary }}
                  >
                    Toevoegen
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  className="flex-1 py-3 rounded-xl ml-2 items-center flex-row justify-center"
                  style={{
                    backgroundColor: awardType === "subtract" ? "#EF4444" : colors.surfaceSecondary,
                  }}
                  onPress={() => setAwardType("subtract")}
                >
                  <Ionicons
                    name="remove-circle"
                    size={20}
                    color={awardType === "subtract" ? "#FFFFFF" : colors.textSecondary}
                  />
                  <Text
                    className="font-semibold ml-2"
                    style={{ color: awardType === "subtract" ? "#FFFFFF" : colors.textSecondary }}
                  >
                    Aftrekken
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Points Input */}
            <View className="mt-6">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Aantal punten *
              </Text>
              <TextInput
                value={points}
                onChangeText={setPoints}
                placeholder="bijv. 10"
                placeholderTextColor={colors.textTertiary}
                keyboardType="number-pad"
                className="p-4 rounded-xl text-xl font-bold text-center"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: awardType === "add" ? "#10B981" : "#EF4444",
                }}
              />
              {points && !isNaN(parseInt(points, 10)) && (
                <View className="mt-2 items-center">
                  <Text style={{ color: colors.textSecondary }}>
                    Nieuwe score:{" "}
                    <Text className="font-bold" style={{ color: colors.text }}>
                      {selectedTeam
                        ? (selectedTeam.total_score + (awardType === "add" ? parseInt(points, 10) : -parseInt(points, 10))).toLocaleString()
                        : selectedUser
                        ? (selectedUser.total_points + (awardType === "add" ? parseInt(points, 10) : -parseInt(points, 10))).toLocaleString()
                        : 0}
                    </Text>{" "}
                    punten
                  </Text>
                </View>
              )}
            </View>

            {/* Reason Input */}
            <View className="mt-6">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Reden *
              </Text>
              <TextInput
                value={reason}
                onChangeText={setReason}
                placeholder="bijv. Gewonnen Kahoot quiz"
                placeholderTextColor={colors.textTertiary}
                multiline
                numberOfLines={3}
                className="p-4 rounded-xl text-base"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                  minHeight: 80,
                  textAlignVertical: "top",
                }}
              />
              <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                Deze reden wordt getoond in de score geschiedenis
              </Text>
            </View>

            {/* Common Reasons */}
            <View className="mt-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Snelle selectie
              </Text>
              <View className="flex-row flex-wrap">
                {[
                  "Kahoot gewonnen",
                  "Speurtocht item",
                  "Foto challenge",
                  "Quiz correct",
                  "Team activiteit",
                  "Bonus punten",
                ].map((quickReason) => (
                  <TouchableOpacity
                    key={quickReason}
                    onPress={() => setReason(quickReason)}
                    className="px-3 py-2 rounded-full mr-2 mb-2"
                    style={{
                      backgroundColor: reason === quickReason ? "#F97316" : colors.surfaceSecondary,
                    }}
                  >
                    <Text
                      className="text-sm"
                      style={{
                        color: reason === quickReason ? "#FFFFFF" : colors.textSecondary,
                      }}
                    >
                      {quickReason}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View className="h-8" />
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

